/**
 * @file AbaRolesGuide.jsx
 * @description Standalone educational SEO page at /aba-roles-guide — the
 *   broadest overview page in the ABA-roles content cluster. Where
 *   RbtVsBtVsBcbaVsBcaba.jsx is the side-by-side comparison table, this page
 *   explains the whole ABA workforce structure (how agencies and providers
 *   at different credential/role levels (BCBA, BCaBA, RBT, BT/ABA therapist)
 *   generally fit together on a case) and what a normal week on a case looks
 *   like. Text that the comparison page already carries is linked, not repeated. Deliberately conservative: no legal or
 *   scope-of-practice advice, no specific numeric requirements (fieldwork
 *   hours, CEU counts, exam question/time counts, ages, fees), no active
 *   case/provider counts, no competitor names, no credential-validity claims
 *   by ABAcases. Mirrors the structure of RbtVsBtVsBcbaVsBcaba.jsx (hero,
 *   visible breadcrumb, role card grid, disclaimer callout, real external
 *   BACB link, gradient closing CTA, PublicFooter).
 * @module components/AbaRolesGuide
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { T } from "../theme";
import {
  UsersIcon, ShieldIcon, EyeIcon, MessageIcon,
  AwardIcon, BriefcaseIcon, BuildingIcon, AlertTriangleIcon, ExternalLinkIcon,
} from "./Icons";
import PublicFooter from "./PublicFooter";
import HeroAlertsCta from "./HeroAlertsCta";
import GuideReviewStamp from "./GuideReviewStamp";
import { GuideYourPath, GuideYourPathChips } from "./GuideShared";
import { useBreadcrumbJsonLd, useSiteSchema, useFaqJsonLd } from "../lib/seoSchema";
import { usePageSeo, seoHref } from "../lib/seo";
import Breadcrumbs from "./Breadcrumbs";

const NAVY = "#0F1024";
const NAVY_MID = "#1a1f3c";
const INDIGO = "#4F46E5";
const INDIGO_LT = "#818cf8";
const BORDER = "rgba(226,232,240,0.8)";
const TEXT_MID = "#475569";

const BACB_URL = "https://www.bacb.com/";
const BACB_EXAM_URL = "https://www.bacb.com/examination-information/";

// Static English data feeding JSON-LD (FAQPage + BreadcrumbList schema). Kept
// unchanged and unlocalized regardless of UI language — search engines index
// schema.org markup independent of the visible/localized page content.
export default function AbaRolesGuide({ onNavigate }) {
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth < 760 : false));

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 760);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  usePageSeo({
    title: "ABA Roles Explained | Agency, BCBA, BCaBA, RBT, and BT",
    description: "Understand how ABA agencies, BCBAs, BCaBAs, RBTs, BTs, and ABA therapists work together, including credentials, supervision, exams, and case responsibilities.",
    canonical: "/aba-roles-guide",
  });
  useSiteSchema(); // global Organization + WebSite
  useBreadcrumbJsonLd("bc-jsonld-aba-roles-guide", [
    { name: "Home", url: "/" },
    { name: "ABA Roles Explained", url: "/aba-roles-guide" },
  ]);
  // Single source of truth: faqDisplay drives the visible cards AND the FAQPage
  // JSON-LD (faqJsonLd below), so schema and visible text can't diverge. faq4's
  // inline BACB link is reconstructed as plain text for the schema.
  const faqDisplay = [
    { q: t('abaRolesGuide.faq1Q'), a: t('abaRolesGuide.faq1A') },
    { q: t('abaRolesGuide.faq2Q'), a: t('abaRolesGuide.faq2A') },
    { q: t('abaRolesGuide.faq3Q'), a: t('abaRolesGuide.faq3A') },
    { q: t('abaRolesGuide.faq4Q'), a: null, externalLink: true },
  ];
  const faqJsonLd = faqDisplay.map((f) => ({
    q: f.q,
    a: f.externalLink
      ? `${t('abaRolesGuide.faq4APre')} ${t('abaRolesGuide.bacbAtBacbComLinkText')}${t('abaRolesGuide.faq4APost')}`
      : f.a,
  }));
  useFaqJsonLd("faq-jsonld-aba-roles-guide", faqJsonLd);

  // Crawlable internal link (real <a href> so Googlebot follows it; preventDefault
  // keeps SPA nav). Same pattern as AboutAbaCases / RbtVsBtVsBcbaVsBcaba.
  const SeoLink = ({ to, style, children }) => (
    <a href={seoHref(to)} onClick={(e) => { e.preventDefault(); onNavigate(to); }} style={{ textDecoration: "none", ...style }}>{children}</a>
  );

  // Real, external, off-domain link to the authoritative BACB source.
  const BacbLink = ({ href = BACB_URL, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: INDIGO, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}>
      {children} <ExternalLinkIcon size={12} color={INDIGO} />
    </a>
  );

  const sectionPad = isMobile ? "44px 18px" : "64px 48px";
  const maxW = 900;

  const roleCards = [
    {
      Icon: BuildingIcon,
      title: t('abaRolesGuide.roleAgencyTitle'),
      what: t('abaRolesGuide.roleAgencyWhat'),
      credential: t('abaRolesGuide.roleAgencyCredential'),
      worksWith: t('abaRolesGuide.roleAgencyWorksWith'),
      confirm: t('abaRolesGuide.roleAgencyConfirm'),
    },
    {
      Icon: AwardIcon,
      title: t('abaRolesGuide.roleBcbaTitle'),
      what: t('abaRolesGuide.roleBcbaWhat'),
      credential: t('abaRolesGuide.roleBcbaCredential'),
      worksWith: t('abaRolesGuide.roleBcbaWorksWith'),
      confirm: t('abaRolesGuide.roleBcbaConfirm'),
    },
    {
      Icon: ShieldIcon,
      title: t('abaRolesGuide.roleBcabaTitle'),
      what: t('abaRolesGuide.roleBcabaWhat'),
      credential: t('abaRolesGuide.roleBcabaCredential'),
      worksWith: t('abaRolesGuide.roleBcabaWorksWith'),
      confirm: t('abaRolesGuide.roleBcabaConfirm'),
    },
    {
      Icon: UsersIcon,
      title: t('abaRolesGuide.roleRbtTitle'),
      what: t('abaRolesGuide.roleRbtWhat'),
      credential: t('abaRolesGuide.roleRbtCredential'),
      worksWith: t('abaRolesGuide.roleRbtWorksWith'),
      confirm: t('abaRolesGuide.roleRbtConfirm'),
    },
    {
      Icon: BriefcaseIcon,
      title: t('abaRolesGuide.roleBtTitle'),
      what: t('abaRolesGuide.roleBtWhat'),
      credential: t('abaRolesGuide.roleBtCredential'),
      worksWith: t('abaRolesGuide.roleBtWorksWith'),
      confirm: t('abaRolesGuide.roleBtConfirm'),
    },
  ];

  return (
    <div style={{ flex: 1, overflowY: "auto", background: "#fff", fontFamily: T.fontBody }}>

      {/* ── Hero ─────────────────────────────────────────────── */}
      <header style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_MID} 100%)`, padding: isMobile ? "28px 18px 44px" : "32px 48px 64px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -120, right: "10%", width: 420, height: 420, borderRadius: "50%", background: "rgba(79,70,229,0.16)", filter: "blur(90px)", pointerEvents: "none" }} />
        <div style={{ position: "relative", maxWidth: maxW, margin: "0 auto" }}>
          {/* Breadcrumb */}
          <Breadcrumbs ariaLabel={t('abaRolesGuide.ariaLabelBreadcrumb')} items={[
            { label: t('abaRolesGuide.breadcrumbHome'), onClick: () => onNavigate("home") },
            { label: t('abaRolesGuide.breadcrumbCurrent') },
          ]} />

          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
            <span style={{ fontSize: 11.5, fontWeight: 800, color: INDIGO_LT, background: "rgba(255,255,255,0.1)", borderRadius: 7, padding: "4px 10px", letterSpacing: "0.02em" }}>{t('abaRolesGuide.badgeLabel')}</span>
            <span style={{ fontSize: 12, color: INDIGO_LT, fontWeight: 600 }}>{t('abaRolesGuide.badgeDomainLabel')}</span>
          </div>

          <h1 style={{ fontSize: isMobile ? 27 : 40, fontWeight: 900, color: "#fff", margin: "0 0 12px", lineHeight: 1.14, letterSpacing: "-0.025em", maxWidth: 760 }}>
            {t('abaRolesGuide.heroTitle')}
          </h1>
          <p style={{ fontSize: isMobile ? 16 : 19, fontWeight: 600, color: "#e2e8f0", lineHeight: 1.4, maxWidth: 700, margin: "0 0 16px" }}>
            {t('abaRolesGuide.heroSubtitle')}
          </p>
          <p style={{ fontSize: isMobile ? 15 : 16.5, color: "#cbd5e1", lineHeight: 1.65, maxWidth: 640, margin: "0 0 24px" }}>
            {t('abaRolesGuide.heroDisclaimer')}
          </p>
          <HeroAlertsCta label={t("guideShared.ctaAlerts")} pagePath="/aba-roles-guide/" onNavigate={onNavigate} isMobile={isMobile} />
        </div>
      </header>
      <GuideYourPathChips current="aba-roles-guide" onNavigate={onNavigate} isMobile={isMobile} />

      {/* ── 1. The ABA team at a glance ───────────────────────── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 23 : 30, fontWeight: 900, color: NAVY, margin: "0 0 18px", letterSpacing: "-0.02em" }}>
            {t('abaRolesGuide.teamGlanceTitle')}
          </h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginBottom: 22 }}>
            {t('abaRolesGuide.teamGlanceP1')}
          </p>

          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: 16 }}>
            {roleCards.map((c) => (
              <div key={c.title} style={{ display: "flex", gap: 14, alignItems: "flex-start", background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 14, padding: "20px 20px", boxShadow: "0 1px 6px rgba(15,16,36,0.04)" }}>
                <div style={{ width: 42, height: 42, borderRadius: 11, background: "#ede9fe", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <c.Icon size={19} color={INDIGO} />
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: NAVY, marginBottom: 6 }}>{c.title}</div>
                  <div style={{ fontSize: 13, color: TEXT_MID, lineHeight: 1.6, marginBottom: 6 }}>{c.what}</div>
                  <div style={{ fontSize: 12, color: TEXT_MID, lineHeight: 1.6, marginBottom: 4 }}><strong style={{ color: NAVY }}>{t('abaRolesGuide.roleCardLabelCredential')}</strong> {c.credential}</div>
                  <div style={{ fontSize: 12, color: TEXT_MID, lineHeight: 1.6, marginBottom: 4 }}><strong style={{ color: NAVY }}>{t('abaRolesGuide.roleCardLabelWorksWith')}</strong> {c.worksWith}</div>
                  <div style={{ fontSize: 12, color: TEXT_MID, lineHeight: 1.6 }}><strong style={{ color: NAVY }}>{t('abaRolesGuide.roleCardLabelConfirm')}</strong> {c.confirm}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 2. ABA agency role ─────────────────────────────────── */}
      <section style={{ background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 18px", letterSpacing: "-0.02em" }}>
            {t('abaRolesGuide.agencyRoleTitle')}
          </h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginBottom: 14 }}>
            {t('abaRolesGuide.agencyRoleP1')}
          </p>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75 }}>
            {t('abaRolesGuide.agencyRoleP2')}
          </p>
        </div>
      </section>

      {/* ── 3. BCBA role ────────────────────────────────────────── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 18px", letterSpacing: "-0.02em" }}>
            {t('abaRolesGuide.bcbaRoleTitle')}
          </h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginBottom: 14 }}>
            {t('abaRolesGuide.bcbaRoleP1')}
          </p>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75 }}>
            {t('abaRolesGuide.bcbaRoleP2')}{" "}
            <BacbLink>{t('abaRolesGuide.bacbLinkText')}</BacbLink>.
          </p>
        </div>
      </section>

      {/* ── 4. BCaBA role ───────────────────────────────────────── */}
      <section style={{ background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 18px", letterSpacing: "-0.02em" }}>
            {t('abaRolesGuide.bcabaRoleTitle')}
          </h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginBottom: 14 }}>
            {t('abaRolesGuide.bcabaRoleP1')}
          </p>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75 }}>
            {t('abaRolesGuide.bcabaRoleP2Pre')}{" "}
            <BacbLink>{t('abaRolesGuide.bacbLinkText')}</BacbLink>{" "}
            {t('abaRolesGuide.bcabaRoleP2Post')}
          </p>
        </div>
      </section>

      {/* ── 5. RBT role ─────────────────────────────────────────── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 18px", letterSpacing: "-0.02em" }}>
            {t('abaRolesGuide.rbtRoleTitle')}
          </h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginBottom: 14 }}>
            {t('abaRolesGuide.rbtRoleP1')}
          </p>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75 }}>
            {t('abaRolesGuide.rbtRoleP2Pre')}{" "}
            <SeoLink to="how-to-become-an-rbt" style={{ color: INDIGO, fontWeight: 700 }}>{t('abaRolesGuide.rbtRoleLinkText')}</SeoLink>.
          </p>
        </div>
      </section>

      {/* ── 6. BT / ABA therapist role ─────────────────────────── */}
      <section style={{ background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 18px", letterSpacing: "-0.02em" }}>
            {t('abaRolesGuide.btRoleTitle')}
          </h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginBottom: 14 }}>
            {t('abaRolesGuide.btRoleP1Pre')} <strong>{t('abaRolesGuide.btRoleP1Strong')}</strong>{t('abaRolesGuide.btRoleP1Post')}
          </p>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75 }}>
            {t('abaRolesGuide.btRoleP2')}
          </p>
        </div>
      </section>

      {/* ── 7. A week on a case ───────────────────────────────── */}
      <section data-testid="week-on-a-case" style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 14px", letterSpacing: "-0.02em" }}>
            {t('abaRolesGuide.weekTitle')}
          </h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginBottom: 20 }}>
            {t('abaRolesGuide.weekIntro')}
          </p>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 16, marginBottom: 20 }}>
            {[
              { Icon: EyeIcon, title: t('abaRolesGuide.weekSeeTitle'), body: t('abaRolesGuide.weekSeeBody') },
              { Icon: MessageIcon, title: t('abaRolesGuide.weekReportTitle'), body: t('abaRolesGuide.weekReportBody') },
              { Icon: BuildingIcon, title: t('abaRolesGuide.weekAgencyTitle'), body: t('abaRolesGuide.weekAgencyBody') },
            ].map((c) => (
              <div key={c.title} style={{ background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 14, padding: "20px 20px", boxShadow: "0 1px 6px rgba(15,16,36,0.04)" }}>
                <div style={{ width: 42, height: 42, borderRadius: 11, background: "#ede9fe", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 12 }}>
                  <c.Icon size={19} color={INDIGO} />
                </div>
                <div style={{ fontSize: 15, fontWeight: 800, color: NAVY, marginBottom: 6 }}>{c.title}</div>
                <div style={{ fontSize: 13.5, color: TEXT_MID, lineHeight: 1.65 }}>{c.body}</div>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 15, color: TEXT_MID, lineHeight: 1.75, marginBottom: 10 }}>
            {t('abaRolesGuide.weekTrackerPre')}{" "}
            <SeoLink to="free-aba-timesheet-payment-tracker" style={{ color: INDIGO, fontWeight: 700 }}>{t('abaRolesGuide.weekTrackerLink')}</SeoLink>{" "}
            {t('abaRolesGuide.weekTrackerPost')}
          </p>
          <p style={{ fontSize: 15, color: TEXT_MID, lineHeight: 1.75 }}>
            {t('abaRolesGuide.weekComparePre')}{" "}
            <SeoLink to="rbt-vs-bt-vs-bcba-vs-bcaba" style={{ color: INDIGO, fontWeight: 700 }}>{t('abaRolesGuide.weekCompareLink')}</SeoLink>{" "}
            {t('abaRolesGuide.weekComparePost')}
          </p>
        </div>
      </section>

      {/* ── 8. Which ABA credentials involve exams? ───────────── */}
      <section style={{ background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 18px", letterSpacing: "-0.02em" }}>
            {t('abaRolesGuide.examsTitle')}
          </h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginBottom: 14 }}>
            {t('abaRolesGuide.examsP1')}
          </p>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75 }}>
            {t('abaRolesGuide.examsP2Pre')}{" "}
            <BacbLink href={BACB_EXAM_URL}>{t('abaRolesGuide.examInfoLinkText')}</BacbLink>{" "}
            {t('abaRolesGuide.examsP2Post')}
          </p>
        </div>
      </section>

      {/* ── Disclaimer ──────────────────────────────────────────── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <div style={{ display: "flex", gap: 14, alignItems: "flex-start", background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 14, padding: isMobile ? "20px" : "24px 26px", boxShadow: "0 1px 6px rgba(15,16,36,0.04)" }}>
            <div style={{ width: 42, height: 42, borderRadius: 11, background: "#fef3c7", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <AlertTriangleIcon size={19} color="#b45309" />
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, color: NAVY, marginBottom: 8 }}>{t('abaRolesGuide.disclaimerHeading')}</div>
              <p style={{ fontSize: 14.5, color: TEXT_MID, lineHeight: 1.7, margin: "0 0 10px" }}>
                {t('abaRolesGuide.disclaimerP1')}
              </p>
              <p style={{ fontSize: 14.5, color: TEXT_MID, lineHeight: 1.7, margin: 0 }}>
                {t('abaRolesGuide.disclaimerP2Pre')}{" "}
                <a href={BACB_URL} target="_blank" rel="noopener noreferrer" style={{ color: INDIGO, fontWeight: 700 }}>
                  {t('abaRolesGuide.bacbFullNameLinkText')} <ExternalLinkIcon size={12} color={INDIGO} style={{ display: "inline", verticalAlign: "middle" }} />
                </a>{t('abaRolesGuide.disclaimerP2Mid')}{" "}
                <BacbLink href="https://www.bacb.com/upcoming-changes/">{t('abaRolesGuide.upcomingChangesLinkText')}</BacbLink>{" "}
                {t('abaRolesGuide.disclaimerP2Post')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── 9. How ABAcases connects roles, credentials, and case fit ── */}
      <section style={{ background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 18px", letterSpacing: "-0.02em" }}>
            {t('abaRolesGuide.connectsTitle')}
          </h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginBottom: 14 }}>
            {t('abaRolesGuide.connectsP1Pre')}
          </p>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75 }}>
            {t('abaRolesGuide.connectsP2Pre1')}{" "}
            <SeoLink to="post-aba-cases" style={{ color: INDIGO, fontWeight: 700 }}>{t('abaRolesGuide.postCasesLinkText')}</SeoLink>{" "}
            {t('abaRolesGuide.connectsP2Mid1')}{" "}
            <SeoLink to="aba-provider-waitlist" style={{ color: INDIGO, fontWeight: 700 }}>{t('abaRolesGuide.joinAlertsLinkText')}</SeoLink>{" "}
            {t('abaRolesGuide.connectsP2Mid2')}{" "}
            <SeoLink to="aba-agencies" style={{ color: INDIGO, fontWeight: 700 }}>{t('abaRolesGuide.staffingLinkText')}</SeoLink>.
          </p>
        </div>
      </section>

      {/* ── 10. FAQ ─────────────────────────────────────────────── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 23 : 30, fontWeight: 900, color: NAVY, margin: "0 0 24px", letterSpacing: "-0.02em", textAlign: "center" }}>
            {t('abaRolesGuide.faqTitle')}
          </h2>
          {faqDisplay.map((f, i) => (
            <div key={i} style={{ background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 12, padding: "18px 20px", marginBottom: 10 }}>
              <div style={{ fontSize: 14.5, fontWeight: 700, color: NAVY, marginBottom: 8 }}>{f.q}</div>
              <div style={{ fontSize: 13.5, color: TEXT_MID, lineHeight: 1.7 }}>
                {f.externalLink ? (
                  <>
                    {t('abaRolesGuide.faq4APre')}{" "}
                    <BacbLink>{t('abaRolesGuide.bacbAtBacbComLinkText')}</BacbLink>
                    {t('abaRolesGuide.faq4APost')}
                  </>
                ) : f.a}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Source/verification note ──────────────────────────── */}
      <div style={{ padding: isMobile ? "20px 18px" : "22px 48px", textAlign: "center", background: "#fff" }}>
        <p style={{ fontSize: 12, color: TEXT_MID, lineHeight: 1.6, maxWidth: 640, margin: "0 auto" }}>
          {t('abaRolesGuide.lastReviewedNote')}
        </p>
      </div>

      <GuideYourPath current="aba-roles-guide" onNavigate={onNavigate} isMobile={isMobile} />

      <GuideReviewStamp slug="aba-roles-guide" />
      <PublicFooter />
    </div>
  );
}
